import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, Loader2 } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { useLeads, type Lead } from "@/hooks/useLeads";
import { useBudgets } from "@/hooks/useBudgets";
import { useAllContracts } from "@/hooks/useContracts";
import { formatDistanceToNowStrict, isAfter, subDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { deriveStage, STAGE_META, type PipelineStage } from "@/lib/pipelineStage";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v, { compact: true });

interface FunnelStep {
  label: string;
  count: number;
  color: string;
  to: string;
  /** Rate relative to the previous step (prototype: "66% dos qualificados"). */
  rate: string;
}

function FunnelBars({ steps }: { steps: FunnelStep[] }) {
  const max = Math.max(...steps.map((s) => s.count), 1);
  return (
    <div className="flex flex-col gap-[11px]">
      {steps.map((s) => (
        <Link key={s.label} to={s.to} className="group flex items-center gap-4">
          <div className="w-[150px] shrink-0 truncate text-[13px] font-semibold">{s.label}</div>
          <div className="relative h-[38px] flex-1 overflow-hidden rounded-[9px] bg-muted">
            <div
              className={`flex h-full items-center rounded-[9px] px-3.5 transition-all group-hover:opacity-85 ${s.color}`}
              style={{ width: `${Math.max((s.count / max) * 100, s.count > 0 ? 8 : 0)}%` }}
            >
              {s.count > 0 && (
                <span className="text-[13px] font-bold text-primary-foreground">{s.count}</span>
              )}
            </div>
          </div>
          <div className="w-[120px] shrink-0 text-right text-[12.5px] font-semibold text-muted-foreground">
            {s.rate}
          </div>
        </Link>
      ))}
    </div>
  );
}

const STAGE_ACTION: Record<PipelineStage, string> = {
  new: "Responder",
  contacted: "Cobrar escopo",
  sent: "Lembrar",
  negotiating: "Ligar",
  closed: "Abrir",
  delivered: "Abrir",
  lost: "Abrir",
};

/** Statuses that count as a real proposal on the table (drafts and rejections don't). */
const VALUED_BUDGET = new Set(["sent", "partially_signed", "signed", "accepted"]);

const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);

export function SalesDashboard() {
  const navigate = useNavigate();
  const { data: leads = [], isLoading: leadsLoading } = useLeads();
  const { data: budgets = [], isLoading: budgetsLoading } = useBudgets();
  const { data: contracts = [], isLoading: contractsLoading } = useAllContracts();

  const now = new Date();
  const threeDaysAgo = subDays(now, 3);

  // Most recent proposal actually sent/closed per lead (same rule as Pipeline).
  const leadValueMap = useMemo(() => {
    const m: Record<string, { value: number; at: number }> = {};
    budgets.forEach((b) => {
      if (!b.lead_id || !VALUED_BUDGET.has(b.status)) return;
      const at = new Date(b.created_at).getTime();
      if (!m[b.lead_id] || at > m[b.lead_id].at) m[b.lead_id] = { value: b.net_total || 0, at };
    });
    return Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v.value]));
  }, [budgets]);

  // Funnel is cumulative: each step counts leads that reached AT LEAST that
  // stage, so the relative rate reads "X% of the previous step".
  const stageOf = useMemo(() => {
    const m = new Map<string, PipelineStage>();
    leads.forEach((l) => m.set(l.id, deriveStage(l, budgets, contracts)));
    return m;
  }, [leads, budgets, contracts]);

  const order: PipelineStage[] = ["new", "contacted", "sent", "negotiating", "closed"];
  // Delivered leads passed through every stage; lost ones count for none.
  const rank = (s: PipelineStage) =>
    s === "lost" ? -1 : s === "delivered" ? order.length - 1 : order.indexOf(s);
  const reached = (stage: PipelineStage) =>
    leads.filter((l) => rank(stageOf.get(l.id) ?? "new") >= order.indexOf(stage)).length;

  const received = leads.length;
  const qualified = reached("contacted");
  const proposal = reached("sent");
  const negotiating = reached("negotiating");
  const signed = reached("closed");

  const funnelSteps: FunnelStep[] = [
    {
      label: "Leads recebidos",
      count: received,
      color: "bg-primary",
      to: "/admin/pipeline",
      rate: "—",
    },
    {
      label: "Qualificados",
      count: qualified,
      color: "bg-primary",
      to: "/admin/pipeline?stage=contacted",
      rate: `${pct(qualified, received)}% do total`,
    },
    {
      label: "Proposta enviada",
      count: proposal,
      color: "bg-warn",
      to: "/admin/pipeline?stage=sent",
      rate: `${pct(proposal, qualified)}% dos qualificados`,
    },
    {
      label: "Em negociação",
      count: negotiating,
      color: "bg-warn",
      to: "/admin/pipeline?stage=negotiating",
      rate: `${pct(negotiating, proposal)}% das propostas`,
    },
    {
      label: "Assinado",
      count: signed,
      color: "bg-ok",
      to: "/admin/pipeline?stage=closed",
      rate: `${pct(signed, negotiating)}% das negociações`,
    },
  ];

  const activeLeads = leads.filter(
    (l) => l.status !== "converted" && l.status !== "lost" && l.status !== "delivered",
  );

  const stuckLeads = useMemo(
    () =>
      activeLeads
        .filter((l) => {
          const stage = stageOf.get(l.id);
          return (
            stage !== "closed" &&
            stage !== "lost" &&
            stage !== "delivered" &&
            !isAfter(new Date(l.updated_at), threeDaysAgo)
          );
        })
        .sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime())
        .slice(0, 8),
    [activeLeads, threeDaysAgo, stageOf],
  );

  const stuckTarget = (lead: Lead, stage: PipelineStage) => {
    switch (stage) {
      case "sent":
      case "negotiating":
        return `/admin/leads/${lead.id}?tab=proposta`;
      default:
        return `/admin/leads/${lead.id}`;
    }
  };

  if (leadsLoading || budgetsLoading || contractsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-[22px]">
      <header>
        <h1 className="mb-1.5 font-display text-[27px] font-bold leading-none tracking-[-0.03em]">
          Relatório de vendas
        </h1>
        <p className="text-[13.5px] text-muted-foreground">
          Do primeiro contato à assinatura — {format(now, "MMMM 'de' yyyy", { locale: ptBR })}
        </p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-[26px] shadow-card">
        <h2 className="mb-[22px] font-display text-[15px] font-bold">Funil de conversão</h2>
        <FunnelBars steps={funnelSteps} />
      </section>

      {stuckLeads.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="flex items-center gap-2.5 border-b border-border px-6 pb-3.5 pt-5">
            <span className="h-[7px] w-[7px] rounded-full bg-warn" />
            <h2 className="font-display text-[15px] font-bold">Parados há mais de 3 dias</h2>
          </div>
          <div>
            {stuckLeads.map((lead) => {
              const stage = stageOf.get(lead.id) ?? "new";
              const value = leadValueMap[lead.id] ?? 0;
              return (
                <div
                  key={lead.id}
                  className="flex items-center gap-4 border-b border-border px-6 py-3.5 transition-colors last:border-0 hover:bg-muted/60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold">{lead.name}</div>
                    <div className="text-[11.5px] text-muted-foreground">
                      {STAGE_META[stage].label}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-[12.5px] text-muted-foreground">
                    {formatDistanceToNowStrict(new Date(lead.updated_at), { locale: ptBR })}
                  </div>
                  <div className="hidden w-[110px] text-right text-[13px] font-bold sm:block">
                    {value > 0 ? fmt(value) : "—"}
                  </div>
                  <Button
                    variant="outline"
                    className="h-8 rounded-lg px-[13px] text-xs font-semibold"
                    onClick={() => navigate(stuckTarget(lead, stage))}
                  >
                    {lead.next_step?.trim()
                      ? lead.next_step.trim().slice(0, 28)
                      : STAGE_ACTION[stage]}
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {stuckLeads.length === 0 && activeLeads.length > 0 && (
        <div className="rounded-2xl border border-ok/25 bg-ok-soft p-6 text-center">
          <p className="text-sm font-semibold text-ok">
            Todos os leads ativos foram atualizados nos últimos 3 dias.
          </p>
        </div>
      )}

      {activeLeads.length === 0 && (
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="Nenhum lead ativo no momento."
          className="rounded-2xl border border-border bg-card p-8 shadow-card"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/pipeline">Ver leads &amp; propostas</Link>
            </Button>
          }
        />
      )}
    </div>
  );
}
